// Normalización del usuario devuelto por PUT /apiV2/usuario/modificarDatosUsuario.
// Módulo puro — sin React, Zustand ni Sentry, mismo patrón y misma prohibición
// de mockearlo en los tests de sus consumidores que `oneclickCards.js` /
// `tiendasVisibles.js`.
//
// El problema que resuelve: ese endpoint devuelve el usuario con OTROS nombres
// de campo que `login` (`nombre` en vez de `usuario_nombre`, `correo` en vez de
// `usuario_correo`, etc. — ver modificarDatosUsuario.put.ts:62-77 vs
// login.post.ts:59-72). `ProfileForm` guardaba esa respuesta tal cual con
// `setUserAuth`, que REEMPLAZA el objeto usuario completo (auth.js), así que
// después de actualizar el perfil todos los `user.usuario_*` quedaban en
// `undefined` y "Datos personales" aparecía en blanco. "Mis pedidos" seguía
// funcionando porque se apoya en `usuario_id`, que es el único campo que el
// endpoint devuelve con el mismo nombre.

// Alias del endpoint de actualización -> nombre canónico (el de `login`, que es
// el que lee toda la app). Los campos que ya vienen con el nombre canónico
// (`usuario_id`, `total_pedidos`, `estado_id`, `usuariotipo_id`,
// `usuario_token`) no necesitan entrada acá: se copian tal cual.
//
// `token` es la excepción: el endpoint devuelve ahí el hash bcrypt de la
// contraseña, y el usuario resultante se persiste en `auth-storage`
// (localStorage, legible por cualquier script). Nunca se copia, y se borra si
// venía de un blob viejo del store.
const CAMPOS_SENSIBLES = ["token"];

const ALIAS_A_CANONICO = {
  rut: "usuario_rut",
  nombre: "usuario_nombre",
  apellido: "usuario_apellido",
  correo: "usuario_correo",
  telefono: "usuario_telefono",
  country: "usuario_country",
};

/**
 * normalizarUsuarioActualizado(resp)
 * Acepta tanto el envelope completo (`{ user, status }`) como el objeto de
 * usuario suelto, porque `ApiCore.put` devuelve `respApiData.data ||
 * respApiData` y este endpoint no usa la clave `data`.
 *
 * Devuelve siempre un objeto con los nombres canónicos. Un alias sólo se
 * traduce si el canónico no vino ya en la respuesta: si el backend algún día
 * empieza a devolver `usuario_nombre`, ese valor manda y el alias no lo pisa.
 * Nunca lanza.
 */
export const normalizarUsuarioActualizado = (resp) => {
  if (!resp || typeof resp !== "object") return {};

  // Si viene la clave `user`, manda esa aunque sea basura: un `{user: null}`
  // es una respuesta rota, no un usuario suelto cuyo único campo se llame
  // "user".
  const fuente = "user" in resp ? resp.user : resp;
  if (!fuente || typeof fuente !== "object") return {};

  const normalizado = {};

  // 1. Todo lo que no sea un alias ni un campo sensible se copia con su propio nombre.
  for (const [clave, valor] of Object.entries(fuente)) {
    if (clave in ALIAS_A_CANONICO || CAMPOS_SENSIBLES.includes(clave)) continue;
    normalizado[clave] = valor;
  }

  // 2. Los alias se traducen, sin pisar un canónico que ya venga en la fuente.
  for (const [alias, canonico] of Object.entries(ALIAS_A_CANONICO)) {
    if (alias in fuente && !(canonico in fuente)) {
      normalizado[canonico] = fuente[alias];
    }
  }

  return normalizado;
};

/**
 * fusionarUsuarioActualizado(actual, resp)
 * Mezcla la respuesta normalizada SOBRE el usuario que ya estaba en el store,
 * en vez de reemplazarlo. Motivo: la respuesta de actualización no trae todos
 * los campos del login, así que un reemplazo pierde datos de sesión aunque los
 * nombres estén bien traducidos.
 *
 * Un campo ausente (`undefined`) conserva el valor previo; un `null` o un `""`
 * sí se escriben, porque son el estado real en base de datos (ej. el usuario
 * borró su teléfono).
 */
export const fusionarUsuarioActualizado = (actual, resp) => {
  const base = actual && typeof actual === "object" ? actual : {};
  const normalizado = normalizarUsuarioActualizado(resp);
  const fusionado = { ...base };

  for (const [clave, valor] of Object.entries(normalizado)) {
    if (valor !== undefined) fusionado[clave] = valor;
  }

  for (const clave of CAMPOS_SENSIBLES) delete fusionado[clave];

  return fusionado;
};
